import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { REQUEST_STATUSES, STATUS_LABELS, STATUS_COLORS, CITIES, SERVICE_TYPES } from "@/lib/constants";
import Shell from "@/components/Shell";
import StatusStepper from "@/components/StatusStepper";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Building2, Wrench, MapPin, Zap, Send, Car, Phone, TrendingUp } from "lucide-react";
import ChatBox from "@/components/ChatBox";
import { ChatAttachment } from "@/components/ChatAttachment";
import { AttachmentsGallery } from "@/components/AttachmentsGallery";
import { WorkPhotos } from "@/components/WorkPhotos";
import PriceItems from "@/components/PriceItems";
import { toast } from "sonner";
import { PartnerEarnings } from "@/components/PartnerEarnings";

export default function WorkshopDashboard() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [quotesByReq, setQuotesByReq] = useState({});
  const [active, setActive] = useState(null);
  const [cityFilter, setCityFilter] = useState("");
  const [serviceFilter, setServiceFilter] = useState("");
  const [qf, setQf] = useState({ parts_price: "", labor_price: "", extra_fees: "0", duration: "", warranty: "", notes: "" });

  const load = async () => {
    const { data } = await api.get("/requests");
    setRequests(data);
    const entries = await Promise.all(
      data.map(async (r) => {
        const q = await api.get(`/requests/${r.id}/quotes`);
        return [r.id, q.data];
      })
    );
    setQuotesByReq(Object.fromEntries(entries));
  };
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);

  const myQuote = (rid) => (quotesByReq[rid] || []).find((q) => q.workshop_id === user.id);

  const submitQuote = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/requests/${active.id}/quotes`, {
        parts_price: Number(qf.parts_price || 0), labor_price: Number(qf.labor_price || 0),
        extra_fees: Number(qf.extra_fees || 0), duration: qf.duration, warranty: qf.warranty, notes: qf.notes,
      });
      toast.success("تم إرسال عرض السعر للعميل");
      setActive(null); setQf({ parts_price: "", labor_price: "", extra_fees: "0", duration: "", warranty: "", notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const updateStatus = async (rid, status) => {
    try { await api.patch(`/requests/${rid}/status`, { status }); toast.success("تم تحديث الحالة"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const newRequests = requests.filter((r) => ["new", "quoted"].includes(r.status) && (!cityFilter || r.city === cityFilter) && (!serviceFilter || r.service_type === serviceFilter));
  const workOrders = requests.filter((r) => r.selected_quote_id && myQuote(r.id)?.status === "accepted");
  const total = workOrders.reduce((s, r) => s + (myQuote(r.id)?.total || 0), 0);

  const stats = [
    { label: "طلبات متاحة", value: newRequests.length, icon: Wrench, color: "text-primary bg-primary/10" },
    { label: "عروضي المرسلة", value: Object.values(quotesByReq).flat().filter((q) => q.workshop_id === user.id).length, icon: Send, color: "text-violet-600 bg-violet-500/10" },
    { label: "أوامر عمل نشطة", value: workOrders.length, icon: Building2, color: "text-emerald-600 bg-emerald-500/10" },
    { label: "إجمالي الأعمال", value: `${total.toFixed(0)} ر.س`, icon: TrendingUp, color: "text-accent bg-accent/10" },
  ];

  return (
    <Shell title={`ورشة ${user?.name}`} subtitle={`${user?.public_id ? `رقم معتمد: ${user.public_id} • ` : ""}استقبل طلبات الصيانة في ${user?.city} وأرسل عروض الأسعار`} icon={Building2} accent="primary"
      actions={
        <div className="flex flex-wrap gap-2">
        <Select value={serviceFilter || "all"} onValueChange={(v) => setServiceFilter(v === "all" ? "" : v)} dir="rtl">
          <SelectTrigger className="w-48 rounded-full" data-testid="service-filter"><SelectValue placeholder="كل الخدمات" /></SelectTrigger>
          <SelectContent><SelectItem value="all">كل الخدمات</SelectItem>{SERVICE_TYPES.map((s) => <SelectItem key={s} value={s} data-testid={`service-filter-${s}`}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={cityFilter || "all"} onValueChange={(v) => setCityFilter(v === "all" ? "" : v)} dir="rtl">
          <SelectTrigger className="w-48 rounded-full" data-testid="city-filter"><SelectValue placeholder="كل المدن" /></SelectTrigger>
          <SelectContent><SelectItem value="all">كل المدن</SelectItem>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        </div>
      }>
      <PartnerEarnings />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((s) => (
          <Card key={s.label} className="p-5 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${s.color}`}><s.icon className="w-6 h-6" /></div>
            <div><div className="text-2xl font-extrabold font-heading">{s.value}</div><div className="text-sm text-muted-foreground">{s.label}</div></div>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="incoming" dir="rtl">
        <TabsList className="h-auto p-1">
          <TabsTrigger value="incoming" className="h-auto py-2" data-testid="tab-incoming">الطلبات الواردة</TabsTrigger>
          <TabsTrigger value="orders" className="h-auto py-2" data-testid="tab-orders">أوامر العمل</TabsTrigger>
        </TabsList>

        <TabsContent value="incoming" className="mt-6">
          {newRequests.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">لا توجد طلبات جديدة حالياً</div>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
              {newRequests.map((r) => {
                const mine = myQuote(r.id);
                return (
                  <Card key={r.id} className="p-5" data-testid={`ws-request-${r.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="font-bold text-lg flex items-center gap-2"><Wrench className="w-4 h-4 text-primary" /> {r.service_type}</div>
                      {r.priority === "urgent" && <Badge className="bg-accent text-white gap-1"><Zap className="w-3 h-3" /> عاجل</Badge>}
                    </div>
                    <div className="text-sm text-muted-foreground flex items-center gap-1 mb-1"><Car className="w-3.5 h-3.5" /> {r.vehicle?.make} {r.vehicle?.model} • {r.vehicle?.year}</div>
                    <div className="text-sm text-muted-foreground flex items-center gap-1 mb-3"><MapPin className="w-3.5 h-3.5" /> {r.location}</div>
                    <p className="text-sm bg-muted/50 rounded-lg p-2 mb-3 line-clamp-3">{r.description}</p>
                    {r.attachments?.length > 0 && (
                      <div className="grid grid-cols-4 gap-1.5 mb-3" data-testid={`ws-request-photos-${r.id}`}>
                        {r.attachments.map((a) => <div key={a.file_id} className="rounded-lg overflow-hidden border border-border text-[10px]"><ChatAttachment att={a} compact /></div>)}
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground mb-3">الحالة: {r.drivable ? "تتحرك" : "متعطلة"} • العميل: {r.customer?.name}</div>
                    {mine ? (
                      <Badge variant="outline" className="w-full justify-center py-2">عرضك: {mine.total} ر.س ({mine.status === "accepted" ? "مقبول" : mine.status === "rejected" ? "مرفوض" : "قيد المراجعة"})</Badge>
                    ) : (
                      <Dialog open={active?.id === r.id} onOpenChange={(o) => setActive(o ? r : null)}>
                        <DialogTrigger asChild>
                          <Button className="w-full rounded-xl gap-2" data-testid={`quote-btn-${r.id}`}><Send className="w-4 h-4" /> إرسال عرض سعر</Button>
                        </DialogTrigger>
                        <DialogContent dir="rtl" className="max-h-[90vh] overflow-auto">
                          <DialogHeader><DialogTitle>عرض سعر — {r.service_type}</DialogTitle></DialogHeader>
                          <form onSubmit={submitQuote} className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                              <div><Label className="mb-1.5 block">سعر قطع الغيار</Label><Input type="number" value={qf.parts_price} onChange={(e) => setQf({ ...qf, parts_price: e.target.value })} data-testid="q-parts" placeholder="0" /></div>
                              <div><Label className="mb-1.5 block">أجور العمل</Label><Input type="number" value={qf.labor_price} onChange={(e) => setQf({ ...qf, labor_price: e.target.value })} data-testid="q-labor" placeholder="0" /></div>
                              <div><Label className="mb-1.5 block">رسوم إضافية</Label><Input type="number" value={qf.extra_fees} onChange={(e) => setQf({ ...qf, extra_fees: e.target.value })} data-testid="q-extra" /></div>
                              <div><Label className="mb-1.5 block">مدة الإصلاح</Label><Input value={qf.duration} onChange={(e) => setQf({ ...qf, duration: e.target.value })} required data-testid="q-duration" placeholder="مثال: يومان" /></div>
                            </div>
                            <div className="p-3 rounded-xl bg-primary/5 text-center">
                              <span className="text-sm text-muted-foreground">الإجمالي: </span>
                              <span className="text-xl font-extrabold text-primary">{(Number(qf.parts_price || 0) + Number(qf.labor_price || 0) + Number(qf.extra_fees || 0)).toFixed(0)} ر.س</span>
                            </div>
                            <div><Label className="mb-1.5 block">الضمان</Label><Input value={qf.warranty} onChange={(e) => setQf({ ...qf, warranty: e.target.value })} data-testid="q-warranty" placeholder="مثال: ضمان 3 أشهر" /></div>
                            <div><Label className="mb-1.5 block">ملاحظات</Label><Textarea value={qf.notes} onChange={(e) => setQf({ ...qf, notes: e.target.value })} data-testid="q-notes" /></div>
                            <DialogFooter><Button type="submit" className="w-full rounded-xl" data-testid="q-submit">إرسال العرض</Button></DialogFooter>
                          </form>
                        </DialogContent>
                      </Dialog>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          {workOrders.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">لا توجد أوامر عمل بعد. سيظهر أمر العمل عند قبول العميل لعرضك.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {workOrders.map((r) => (
                <Card key={r.id} className="p-5" data-testid={`order-${r.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="font-bold text-lg">{r.service_type}</div>
                      <div className="text-sm text-muted-foreground flex items-center gap-1"><Car className="w-3.5 h-3.5" /> {r.vehicle?.make} {r.vehicle?.model} • {r.vehicle?.plate}</div>
                      <div className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5"><Phone className="w-3.5 h-3.5" /> {r.customer?.phone}</div>
                    </div>
                    <Badge variant="outline" className={STATUS_COLORS[r.status]}>{STATUS_LABELS[r.status]}</Badge>
                  </div>
                  <div className="mb-4"><StatusStepper statuses={REQUEST_STATUSES.slice(2)} current={r.status} /></div>
                  <div>
                    <Label className="mb-1.5 block text-sm">تحديث الحالة</Label>
                    <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)} dir="rtl">
                      <SelectTrigger data-testid={`status-select-${r.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {REQUEST_STATUSES.slice(2).map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="mt-4">
                    <div className="font-semibold text-sm mb-2">بنود تسعير إضافية (عمل إضافي / قطع غيار)</div>
                    <PriceItems requestId={r.id} role="workshop" />
                  </div>
                  <div className="mt-4">
                    <div className="font-semibold text-sm mb-2">محادثة مع العميل</div>
                    <ChatBox threadType="request" refId={r.id} meId={user.id} />
                  </div>
                  <div className="mt-4">
                    <WorkPhotos requestId={r.id} canEdit={!["delivered", "closed"].includes(r.status)} />
                  </div>
                  <div className="mt-4">
                    <AttachmentsGallery requestId={r.id} />
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </Shell>
  );
}
